import { useState } from 'react';

// ========== USAGE WIDGET (for Student Home) ==========
export const UsageWidget = ({ usage }) => {
  if (!usage) return null;

  const sessionPercentage = ((usage?.daily_requests || 0) / (usage?.daily_limit || 1)) * 100;
  const tokenPercentage = (usage?.monthly_tokens_limit || 0) > 0 ? ((usage?.monthly_tokens || 0) / (usage?.monthly_tokens_limit || 1)) * 100 : 0;
  
  const isLowSessions = usage.daily_limit > 0 && usage.daily_remaining <= 1;
  const isLowTokens = usage.monthly_tokens_limit > 0 && (usage.monthly_tokens_remaining / usage.monthly_tokens_limit) < 0.1;

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'k';
    return num;
  };

  return (
    <div className={`rounded-3xl p-5 ${(isLowSessions || isLowTokens) ? 'bg-red-100 border-2 border-red-400' : 'bg-white border-2 border-purple-100'} shadow-2xl min-w-[280px]`}>
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm font-black uppercase tracking-wider text-gray-900">AI Naudojimas</span>
        {(isLowSessions || isLowTokens) && <span className="text-[10px] px-2 py-0.5 bg-red-500 text-white rounded-full animate-pulse">RIBOTI</span>}
      </div>

      {/* Sessions */}
      <div className="mb-4">
        <div className="flex justify-between items-end mb-1">
          <span className="text-xs font-bold text-gray-700">Šiandien liko užklausų:</span>
          <span className="text-2xl font-black text-blue-700">{usage?.daily_remaining ?? 0} / {usage?.daily_limit === -1 ? '∞' : (usage?.daily_limit ?? 0)}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all duration-1000 ${isLowSessions ? 'bg-red-500' : 'bg-blue-600'}`}
            style={{ width: `${usage?.daily_limit === -1 ? 100 : Math.max(0, (usage?.daily_remaining / usage?.daily_limit) * 100)}%` }}
          />
        </div>
      </div>

      {/* Tokens */}
      <div className="mb-4">
        <div className="flex justify-between items-end mb-1">
          <span className="text-xs font-bold text-gray-700">Mėnesio tokenų likutis:</span>
          <span className="text-sm font-black text-purple-700">
            {formatNumber(usage?.monthly_tokens_remaining || 0)} / {usage?.monthly_tokens_limit === -1 ? '∞' : formatNumber(usage?.monthly_tokens_limit || 0)}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all duration-1000 ${isLowTokens ? 'bg-red-500' : 'bg-purple-600'}`}
            style={{ width: `${usage?.monthly_tokens_limit === -1 ? 100 : Math.max(0, (usage?.monthly_tokens_remaining / usage?.monthly_tokens_limit) * 100)}%` }}
          />
        </div>
      </div>

      {(isLowSessions || isLowTokens) && (
        <a
          href="/subscription"
          className="block text-center text-xs font-black text-red-600 hover:text-red-700 transition"
        >
          PAGERINTI PLANĄ →
        </a>
      )}
    </div>
  );
};

// ========== COMPACT USAGE (for header inline) ==========
export const UsageWidgetCompact = ({ usage }) => {
  if (!usage) return null;

  const isLow = usage.daily_limit > 0 && usage.daily_remaining <= 1;

  return (
    <div
      className={`usage-compact ${isLow ? 'low' : ''}`}
      title={`Šiandien liko: ${usage.daily_remaining ?? 0} / ${usage.daily_limit === -1 ? '∞' : usage.daily_limit ?? 0} | Mėnesio tokenai: ${usage.monthly_tokens_remaining ?? 0}`}
    >
      <span>🤖</span>
      <span>{usage.daily_remaining ?? 0}{usage.daily_limit !== -1 ? `/${usage.daily_limit}` : ''}</span>
      {isLow && <span className="text-[9px] uppercase">Mažai!</span>}
    </div>
  );
};

// ========== UPGRADE MODAL ==========
export const UpgradeModal = ({ isOpen, onClose, currentTier = 'free', requiredLevel = 1 }) => {
  if (!isOpen) return null;

  const levelNames = {
    0: 'Nemokamas',
    1: 'Basic',
    2: 'Premium / Family'
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-3xl"
          aria-label="Uždaryti"
        >
          ×
        </button>

        <div className="text-center mb-6">
          <div className="text-7xl mb-4">{requiredLevel === 2 ? '👑' : '🚀'}</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Reikalingas {levelNames[requiredLevel]} planas!
          </h2>
          <p className="text-gray-600">
            Šis žaidimas ar funkcija prieinama tik su aukštesnio lygio prenumerata.
          </p>
        </div>

        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-6 mb-6">
          <h3 className="font-bold text-gray-800 mb-3">Ką gausite atsinaujinę:</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>✓ Padidintas AI tokenų biudžetas</li>
            <li>✓ Daugiau užklausų kiekvieną dieną</li>
            <li>✓ Prieiga prie visų edukacinių žaidimų</li>
            <li>✓ Personalizuoti mokymosi planai</li>
            <li>✓ Prioritetinis palaikymas</li>
          </ul>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition"
          >
            Vėliau
          </button>
          <a
            href="/subscription"
            className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-bold text-center shadow-lg hover:shadow-xl transition transform hover:scale-105"
          >
            Pagerinti 🚀
          </a>
        </div>
      </div>
    </div>
  );
};

// ========== PRICING CARD (išskirtas iš plano kortelės) ==========
export const PricingCard = ({ plan, currentTier, onSubscribe, loading, featureNames }) => {
  const isCurrent = currentTier === plan.tier;
  const isHighlighted = plan.highlighted;

  return (
    <div
      className={`rounded-3xl shadow-2xl overflow-hidden transition transform hover:scale-105 ${isHighlighted ? 'border-4 border-purple-500 ring-4 ring-purple-200' : 'bg-white'
        }`}
    >
      {isHighlighted && (
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-center py-2 font-bold">
          🌟 POPULIARIAUSIA
        </div>
      )}

      <div className="p-8">
        <h3 className="text-3xl font-bold text-gray-800 mb-2">{plan.name}</h3>

        <div className="mb-6">
          <span className="text-5xl font-bold text-gray-900">
            {!plan.price || plan.price === 0 ? 'Nemokama' : `€${plan.price}`}
          </span>
          {plan.price > 0 && <span className="text-gray-600">/{plan.period || 'mėn'}</span>}
        </div>

        {/* Limits - Removed as they are now in features list for better flexibility */}

        {/* Features */}
        <div className="mb-6 space-y-2">
          {plan.features.map((feature, idx) => (
            <div key={idx} className="flex items-start text-sm text-gray-700">
              <span className="mr-2">✓</span>
              <span>{featureNames[feature] || feature}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <button
          onClick={() => onSubscribe(plan.tier)}
          disabled={loading || isCurrent}
          className={`w-full py-4 rounded-2xl font-bold text-lg transition shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${isHighlighted
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
              : isCurrent
                ? 'bg-gray-300 text-gray-600'
                : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
            }`}
        >
          {isCurrent
            ? '✓ Dabartinis planas'
            : !plan.price || plan.price === 0
              ? 'Pradėti nemokamai'
              : 'Prenumeruoti'}
        </button>
      </div>
    </div>
  );
};